// Sample of the H&S Template Library as shown on the marketing site. Titles mirror the categories and
// course names used in fixlr.app; counts are indicative of the live library (v148).
import { GraduationCap, FileWarning, ShieldAlert, HardHat, FlaskConical, ClipboardCheck, KeyRound, ScrollText, Flame } from "lucide-react";

export const LIBRARY_VERSION = "v148";
export const LIBRARY_TOTAL = 312;
export const LIBRARY_ICONS = { training: GraduationCap, rams: FileWarning, risk: ShieldAlert, talks: HardHat, coshh: FlaskConical, checklists: ClipboardCheck, permits: KeyRound, policies: ScrollText, fire: Flame };

export const LIBRARY_CATEGORIES = [
  { key: "all", label: "All", count: LIBRARY_TOTAL },
  { key: "training", label: "Training courses", count: 38, blurb: "Short courses with a quiz — pass mark 80%, a pass adds the qualification to your Competency matrix." },
  { key: "rams", label: "RAMS", count: 64, blurb: "Risk assessment & method statement packs per task, in a template you can brand." },
  { key: "risk", label: "Risk assessments", count: 57, blurb: "Stand-alone 5×5 risk assessments, including point-of-work assessments for the phone." },
  { key: "talks", label: "Toolbox talks", count: 46, blurb: "Five-minute briefings with acknowledgements and signature capture." },
  { key: "coshh", label: "COSHH", count: 29, blurb: "Substance assessments: storage, use, disposal, first aid, spill response." },
  { key: "checklists", label: "Inspection checklists", count: 33, blurb: "Pass / fail / N/A items with photo-on-fail; critical items fail the inspection." },
  { key: "permits", label: "Permits", count: 12, blurb: "Permit-to-work templates checked against the Competency matrix when issued." },
  { key: "policies", label: "Policies & statements", count: 20, blurb: "Company policies and statements, ready to publish to client portals." },
  { key: "fire", label: "Fire & CDM", count: 13, blurb: "Fire risk assessment, fire safety documents and CDM 2015 paperwork." },
];

export const LIBRARY_TRADES = ["All trades", "General", "Electrical", "Gas & heating", "Plumbing", "Roofing", "Lifts", "Fire", "Cleaning & FM", "Grounds"];

const OUTLINES = {
  training: ["Why it matters (2 min video)", "The law in plain English", "Hazards you will meet on site", "Controls and what good looks like", "Quiz — 10 questions, pass mark 80%", "Certificate + Competency matrix entry"],
  rams: ["Scope of works & site details", "Hazards, who's at risk, 5×5 initial rating", "Controls & residual rating", "Method statement — step by step", "PPE, COSHH, equipment, permits", "Approver sign-off · operative acknowledgement"],
  risk: ["Activity & location", "Hazards identified", "Who might be harmed and how", "Existing controls · likelihood × severity", "Further action, owner, due date", "Review date"],
  talks: ["What this talk covers", "Key points (5–7 bullets)", "Real incident example", "Do / don't on this site", "Acknowledgements", "Attendance & signatures"],
  coshh: ["Substance, supplier, SDS reference", "Hazard classification & route of exposure", "Storage, use and disposal", "Controls & PPE", "First aid and spill response", "Review date"],
  checklists: ["Pre-start & documentation", "Equipment & condition items", "Critical safety items (fail = inspection fails)", "Housekeeping & environment", "Photo-on-fail prompts", "Score & sign-off"],
  permits: ["Permit scope, location, duration", "Competency check (matrix)", "Isolations & precautions", "Issue · acceptance · hand-back", "Cancellation & close-out", "PDF record"],
  policies: ["Statement of intent", "Responsibilities", "Arrangements", "Review & communication", "Version & approval"],
  fire: ["Premises & occupancy", "Fire hazards & ignition sources", "People at risk", "Means of escape, detection, fighting", "Significant findings & action plan", "Review date"],
};

const mk = (cat, items) => items.map(([title, trade, version, copies, badge, mins]) => ({ id: `${cat}-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`, cat, title, trade, version, copies, badge: badge || "", mins: mins || null, outline: OUTLINES[cat] }));

export const LIBRARY_ITEMS = [
  ...mk("training", [
    ["Asbestos awareness", "General", "v3", 1866, "", 25],
    ["COSHH awareness", "General", "v2", 1204, "", 20],
    ["Working at height — ladders & podiums", "General", "v4", 2130, "Updated", 25],
    ["Manual handling for engineers", "General", "v2", 1711, "", 20],
    ["Electrical safety for non-electricians", "Electrical", "v2", 980, "", 20],
    ["Legionella awareness", "Plumbing", "v3", 1402, "", 25],
    ["Lone working & personal safety", "General", "v1", 1098, "New", 15],
    ["Fire safety & evacuation", "Fire", "v3", 1655, "", 20],
    ["Driving for work & van safety", "General", "v2", 1320, "", 20],
    ["Slips, trips and falls on client sites", "Cleaning & FM", "v1", 877, "", 15],
    ["Gas safety awareness for FM teams", "Gas & heating", "v2", 640, "", 25],
    ["Site induction — occupied residential buildings", "General", "v2", 1510, "", 15],
  ]),
  ...mk("rams", [
    ["Working at height — roof-level gutter & fascia works", "Roofing", "v4", 1204, "Updated"],
    ["Boiler service & repair in a plant room", "Gas & heating", "v3", 1587, ""],
    ["Consumer unit replacement in an occupied flat", "Electrical", "v2", 1322, ""],
    ["Lift motor room — maintenance visit", "Lifts", "v2", 412, ""],
    ["Fire door inspection & remedial works", "Fire", "v3", 688, ""],
    ["Hot works — torch-on roofing", "Roofing", "v2", 530, ""],
    ["Legionella flushing & temperature monitoring", "Plumbing", "v2", 944, ""],
    ["Emergency lighting test & replacement", "Electrical", "v1", 702, "New"],
    ["External cleaning — reach & wash", "Cleaning & FM", "v1", 356, ""],
    ["Hedge cutting & grounds maintenance", "Grounds", "v2", 290, ""],
    ["Drain jetting & CCTV survey", "Plumbing", "v2", 471, ""],
    ["EICR testing in communal areas", "Electrical", "v3", 1150, ""],
  ]),
  ...mk("risk", [
    ["Point-of-work risk assessment (phone)", "General", "v2", 3210, "Updated"],
    ["Work in plant rooms & risers", "General", "v2", 980, ""],
    ["Lone working — out of hours call-outs", "General", "v1", 1140, ""],
    ["Working near fragile roofs & skylights", "Roofing", "v3", 420, ""],
    ["Use of MEWPs & scissor lifts", "General", "v2", 512, ""],
    ["Hand-arm vibration — power tools", "General", "v1", 377, ""],
    ["Silica dust — chasing & cutting", "General", "v2", 455, ""],
    ["Working in void properties", "General", "v1", 606, "New"],
    ["Pressure systems & hot water", "Gas & heating", "v2", 388, ""],
  ]),
  ...mk("talks", [
    ["Working at height", "General", "v3", 2402, "", 5],
    ["Hot works", "Roofing", "v2", 1130, "", 5],
    ["Lone working", "General", "v1", 1022, "New", 4],
    ["Asbestos — stop and report", "General", "v2", 1870, "", 5],
    ["Manual handling — two-person lifts", "General", "v2", 1540, "", 4],
    ["Ladder safety — three points of contact", "General", "v3", 2015, "", 4],
    ["Working in occupied homes", "General", "v1", 988, "", 5],
    ["Winter driving & gritting", "Grounds", "v2", 610, "", 4],
    ["Heat stress & hydration", "General", "v1", 455, "", 4],
    ["Electrical isolation — prove dead", "Electrical", "v2", 1205, "", 5],
    ["Legionella — why we flush", "Plumbing", "v1", 702, "", 4],
    ["Near misses — report everything", "General", "v2", 1318, "", 4],
  ]),
  ...mk("coshh", [
    ["Solvent cleaners & degreasers", "Cleaning & FM", "v5", 947, "Updated"],
    ["Bitumen roofing compounds", "Roofing", "v1", 88, "New"],
    ["Drain cleaning — sodium hydroxide", "Plumbing", "v2", 512, ""],
    ["Boiler inhibitor & system cleaner", "Gas & heating", "v2", 760, ""],
    ["Pesticides & weedkiller (glyphosate)", "Grounds", "v3", 330, ""],
    ["Expanding foam & sealants", "General", "v2", 1021, ""],
    ["Lift machine oils & greases", "Lifts", "v1", 140, ""],
    ["Bleach & chlorine-based cleaners", "Cleaning & FM", "v3", 1322, ""],
  ]),
  ...mk("checklists", [
    ["Ladder & tower pre-use check", "General", "v2", 1530, ""],
    ["Van & vehicle weekly check", "General", "v3", 1922, ""],
    ["Plant room inspection", "Gas & heating", "v2", 880, ""],
    ["Fire door quarterly check", "Fire", "v3", 1204, "Updated"],
    ["Emergency lighting monthly flick test", "Electrical", "v2", 1110, ""],
    ["Communal area H&S walk", "Cleaning & FM", "v1", 740, ""],
    ["Power tool & PAT visual check", "General", "v2", 980, ""],
    ["Site welfare & first-aid kit check", "General", "v1", 655, ""],
    ["Lift landing & car weekly check", "Lifts", "v1", 210, "New"],
  ]),
  ...mk("permits", [
    ["Hot works permit", "General", "v3", 512, ""],
    ["Permit to work — electrical isolation", "Electrical", "v2", 441, ""],
    ["Confined space entry", "General", "v2", 196, ""],
    ["Roof access permit", "Roofing", "v1", 308, "New"],
    ["Plant-room access permit", "Gas & heating", "v1", 88, ""],
    ["Working at height permit", "General", "v2", 377, ""],
  ]),
  ...mk("policies", [
    ["Health & Safety Policy", "General", "v4", 2210, ""],
    ["Anti-Bribery Statement", "General", "v2", 1105, ""],
    ["Modern Slavery & Human Trafficking Statement", "General", "v2", 1040, ""],
    ["Client Privacy Policy", "General", "v3", 1322, ""],
    ["Personal Data Handling Policy", "General", "v2", 980, ""],
    ["Corporate Social Responsibility Policy", "General", "v1", 612, ""],
    ["Company sick pay policy", "General", "v2", 890, ""],
    ["Drug & alcohol policy", "General", "v1", 702, "New"],
    ["Environmental policy", "General", "v2", 655, ""],
  ]),
  ...mk("fire", [
    ["Fire risk assessment — residential block (communal areas)", "Fire", "v3", 1402, "Updated"],
    ["Fire risk assessment — small commercial unit", "Fire", "v2", 880, ""],
    ["Fire safety document — evacuation procedure", "Fire", "v2", 1210, ""],
    ["CDM 2015 — construction phase plan (small works)", "General", "v2", 510, ""],
    ["CDM 2015 — pre-construction information", "General", "v1", 377, ""],
    ["Fire warden duties & checklist", "Fire", "v2", 940, ""],
  ]),
];
